import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

interface NOVAREPowerDetailProps {
  t: any;
  isRTL: boolean;
}

const ProductDetailPage = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 120px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
  padding-bottom: 4rem;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Breadcrumbs = styled(motion.div)<{ $isRTL: boolean }>`
  padding: 1rem 0;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 1.5rem;
  }
`;

const BreadcrumbLink = styled.span<{ $isLast: boolean }>`
  color: ${props => props.$isLast ? '#dc2626' : '#64748b'};
  text-decoration: none;
  font-weight: ${props => props.$isLast ? '600' : '400'};
  cursor: ${props => props.$isLast ? 'default' : 'pointer'};

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    text-decoration: ${props => props.$isLast ? 'none' : 'underline'};
  }
`;

const BreadcrumbSeparator = styled.span`
  color: #94a3b8;
  margin: 0 0.5rem;
`;

const ProductLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
`;

const MainImage = styled.div`
  width: 100%;
  max-width: 500px;
  height: 400px;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ThumbnailImage = styled.div`
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    border-color: #dc2626;
    transform: translateY(-2px);
  }
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    align-items: center;
  }
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const TechnicalDatasheet = styled(motion.div)<{ $isRTL: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 300px;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const PDFIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #dc2626;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
`;

const PDFText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PDFLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
`;

const PDFSubtext = styled.span`
  font-size: 0.8rem;
  color: #64748b;
`;

const ProviderLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 300px;
  
  img {
    max-width: 120px;
    height: auto;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    max-width: 250px;
    align-self: center;
    margin: 1rem auto;
    
    img {
      max-width: 100px;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  border: none;
  
  ${props => props.$variant === 'primary' ? `
    background: #dc2626;
    color: white;
    
    @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
      background: #b91c1c;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
    }
  ` : `
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
    
    @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
      background: #e2e8f0;
      transform: translateY(-1px);
    }
  `}
`;

const TabsSection = styled.div`
  margin-top: 3rem;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 2rem;
  gap: 0.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 1rem 1.5rem;
  border: none;
  background: none;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.$active ? '#dc2626' : '#64748b'};
  border-bottom: 2px solid ${props => props.$active ? '#dc2626' : 'transparent'};
  transition: all 0.3s ease;
  font-size: 1rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    color: #dc2626;
  }
`;

const TabContent = styled(motion.div)`
  min-height: 400px;
`;

const DescriptionSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  text-align: left;
  direction: ltr;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: left;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #dc2626;
  padding-bottom: 0.5rem;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    text-align: left;
  }
`;

const DescriptionText = styled.p`
  color: #475569;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  text-align: left;
  
  @media (max-width: 768px) {
    text-align: left;
    font-size: 0.95rem;
  }
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const SpecCard = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #dc2626;
  text-align: left;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const SpecCardTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-align: left;

  &::before {
    content: "⚡";
    font-size: 1.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: left;
  }
`;

const SpecCardContent = styled.div`
  color: #475569;
  line-height: 1.6;
  font-size: 0.95rem;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-align: left;
  }
`;

const EquipmentSection = styled.div`
  background: #f8f9fa;
  padding: 2rem;
  border-radius: 12px;
  margin-top: 2rem;
  border: 1px solid #e2e8f0;
`;

const EquipmentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const EquipmentItem = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #dc2626;
  font-size: 0.9rem;
  color: #475569;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const ResourcesSection = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    background: #e2e8f0;
    transform: translateY(-1px);
  }
`;

const ResourceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ResourceName = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const ResourceSize = styled.span`
  font-size: 0.9rem;
  color: #64748b;
`;

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #dc2626;
  text-decoration: none;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1.25rem;
    text-align: left;
  }
  
  &:hover {
    background: #dc2626;
    color: white;
  }
`;

const NOVAREPowerDetail: React.FC<NOVAREPowerDetailProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedImage, setSelectedImage] = useState(0);

  const breadcrumbs = [
    { label: "Catalogue produits", path: "/" },
    { label: "Transformateurs", path: "/category/transformateurs" },
    { label: "Transformateurs de puissance", path: "/category/transformateurs" },
    { label: "NOVARE Power", path: "" }
  ];

  const images = [
    '/images/catalog/transformateurs/NOVARE Power.jpg',
    '/images/catalog/transformateurs/NOVARE Power.jpg'
  ];

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const handleDatasheetClick = () => {
    window.open('/images/catalog/transformateurs/NOVARE Power/Nexans_NOVARE-Power.pdf', '_blank');
  };

  return (
    <ProductDetailPage $isRTL={isRTL}>
      <Container>
        <Breadcrumbs
          $isRTL={isRTL}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbLink
                $isLast={index === breadcrumbs.length - 1}
                onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              >
                {breadcrumb.label}
              </BreadcrumbLink>
              {index < breadcrumbs.length - 1 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
            </React.Fragment>
          ))}
        </Breadcrumbs>

        <ProductLayout>
          <ImageSection>
            <MainImage>
              <img src={images[selectedImage]} alt="NOVARE Power" />
            </MainImage>
            <ThumbnailImage onClick={() => setSelectedImage(0)}>
              <img src={images[0]} alt="NOVARE Power thumbnail" />
            </ThumbnailImage>
          </ImageSection>

          <ProductInfo>
            <ProductTitle>NOVARE Power</ProductTitle>
            <TechnicalDatasheet
              $isRTL={isRTL}
              onClick={handleDatasheetClick}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <PDFIcon>PDF</PDFIcon>
              <PDFText>
                <PDFLabel>Fiche technique</PDFLabel>
                <PDFSubtext>PDF</PDFSubtext>
              </PDFText>
            </TechnicalDatasheet>

            <ProviderLogo>
              <img 
                src="/images/partners/nexans.png" 
                alt="Nexans" 
              />
            </ProviderLogo>
          </ProductInfo>
        </ProductLayout>

        <TabsSection ref={ref}>
          <TabsHeader>
            <TabButton 
              $active={activeTab === 'description'} 
              onClick={() => setActiveTab('description')}
            >
              Description
            </TabButton>
            <TabButton 
              $active={activeTab === 'ressources'} 
              onClick={() => setActiveTab('ressources')}
            >
              Ressources
            </TabButton>
          </TabsHeader>

          <TabContent>
            {activeTab === 'description' && (
              <DescriptionSection>
                <SectionTitle>Description</SectionTitle>
                <DescriptionText>
                  Les transformateurs NOVARE Power sont fabriqués par NEXANS MAROC, offrant des solutions de haute qualité pour les applications de puissance industrielle et de distribution.
                </DescriptionText>
                
                <SpecsGrid>
                  <SpecCard>
                    <SpecCardTitle>Puissance</SpecCardTitle>
                    <SpecCardContent>
                      <strong>Puissances usuelles:</strong> 5-10-20-36 et 47 MVA<br/>
                      <strong>Autres puissances disponibles:</strong> 7.5-12 et 15 MVA, jusqu'à 54 MVA
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Tension</SpecCardTitle>
                    <SpecCardContent>
                      <strong>HT:</strong> 60 ou 63 KV<br/>
                      <strong>MT:</strong> De 5.5 jusqu'à 22 KV en charge
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Fréquence</SpecCardTitle>
                    <SpecCardContent>50 Hz</SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Groupes de couplage</SpecCardTitle>
                    <SpecCardContent>YNynO-YNd11</SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Refroidissement</SpecCardTitle>
                    <SpecCardContent>
                      <strong>Système ONAN:</strong> Circulation naturelle d'huile dans les radiateurs refroidis par l'air ambiant<br/>
                      <strong>Système ONAF:</strong> Circulation d'air par ventilateurs motorisés<br/>
                      <strong>Système ODAF:</strong> Circulation forcée d'huile dans un aéro-réfrigérant
                    </SpecCardContent>
                  </SpecCard>
                  
                  <SpecCard>
                    <SpecCardTitle>Réglage de tension</SpecCardTitle>
                    <SpecCardContent>
                      Effectué sur l'enroulement haute tension, disponible en deux conceptions:<br/>
                      <strong>Réglage hors tension:</strong> Commutateur de prises à commande manuelle avec 3 ou 5 positions<br/>
                      <strong>Réglage en charge:</strong> Gamme complète de 17 ou 21 étapes
                    </SpecCardContent>
                  </SpecCard>
                </SpecsGrid>

                <SectionTitle>NOVARE Power - Constitution</SectionTitle>
                <DescriptionText>
                  Les transformateurs NOVARE Power sont équipés d'équipements de base standard et d'options personnalisables selon les besoins spécifiques du client.
                </DescriptionText>
                
                <EquipmentSection>
                  <SectionTitle style={{ marginBottom: '1rem', borderBottom: 'none', paddingBottom: '0' }}>
                    Équipements de base
                  </SectionTitle>
                  <EquipmentGrid>
                    <EquipmentItem>Connexion HTB et HTA: Traversées en porcelaine</EquipmentItem>
                    <EquipmentItem>1 Commutateur de prises hors tension</EquipmentItem>
                    <EquipmentItem>4 Rouleaux plats ou à brides orientables</EquipmentItem>
                    <EquipmentItem>1 Ouverture de remplissage et traitement</EquipmentItem>
                    <EquipmentItem>1 Bouchon de vidange</EquipmentItem>
                    <EquipmentItem>1 Plaque signalétique</EquipmentItem>
                    <EquipmentItem>Anneaux de levage</EquipmentItem>
                    <EquipmentItem>2 Points de mise à la terre</EquipmentItem>
                    <EquipmentItem>Traitement de surface</EquipmentItem>
                    <EquipmentItem>1 Réservoir d'expansion d'huile équipé d'un déshydrateur d'air à gel de silice et d'une vanne de vidange</EquipmentItem>
                    <EquipmentItem>Indicateur de niveau d'huile visuel ou à contact</EquipmentItem>
                    <EquipmentItem>Vannes de vidange et traitement d'huile</EquipmentItem>
                  </EquipmentGrid>
                </EquipmentSection>

                <DescriptionText style={{ marginTop: '2rem', fontStyle: 'italic', color: '#64748b' }}>
                  <strong>Note:</strong> Des études de conception spéciales sont nécessaires pour répondre aux besoins spécifiques du client.
                </DescriptionText>
              </DescriptionSection>
            )}

            {activeTab === 'ressources' && (
              <ResourcesSection>
                <SectionTitle>Ressources</SectionTitle>
                
                <ResourceItem>
                  <ResourceInfo>
                    <ResourceName>Catalogue Transformateurs NOVARE</ResourceName>
                    <ResourceSize>PDF - 1.7 MB</ResourceSize>
                  </ResourceInfo>
                  <DownloadLink 
                    href="/images/catalog/transformateurs/NOVARE Power/ressources/Catalogue Transformateurs NOVARE_11.pdf"
                    target="_blank"
                  >
                    <span>↓</span>
                    Télécharger
                  </DownloadLink>
                </ResourceItem>
              </ResourcesSection>
            )}
          </TabContent>
        </TabsSection>
      </Container>
    </ProductDetailPage>
  );
};

export default NOVAREPowerDetail; 